import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ScrollText, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { legalAPI } from '@/services/api';

/**
 * Floating bottom banner that prompts logged-in users to re-accept the
 * Terms & Conditions / Privacy Policy whenever the admin publishes a new
 * version. Polled once on mount and again on route change so it picks up
 * fresh version bumps without a full page reload.
 */
export default function ConsentBanner() {
	const { t } = useTranslation();
	const { user, token } = useAuth();
	const location = useLocation();
	const [needsReaccept, setNeedsReaccept] = useState(false);
	const [agreed, setAgreed] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [justAccepted, setJustAccepted] = useState(false);

	// Re-check status on login + every navigation
	useEffect(() => {
		if (!token || !user) {
			setNeedsReaccept(false);
			setJustAccepted(false);
			return;
		}
		let cancelled = false;
		legalAPI
			.consentStatus()
			.then((r) => {
				if (!cancelled) setNeedsReaccept(!!r.data?.needs_reaccept);
			})
			.catch(() => {
				if (!cancelled) setNeedsReaccept(false);
			});
		return () => { cancelled = true; };
	}, [token, user, location.pathname]);

	const submit = async () => {
		setSubmitting(true);
		try {
			await legalAPI.consentAccept();
			setJustAccepted(true);
			setNeedsReaccept(false);
		} catch {
			// intentionally silent — banner remains visible so the user can retry
		} finally {
			setSubmitting(false);
		}
	};

	// Auto-hide the brief "accepted" confirmation after 4s
	useEffect(() => {
		if (!justAccepted) return;
		const tid = setTimeout(() => setJustAccepted(false), 4000);
		return () => clearTimeout(tid);
	}, [justAccepted]);

	if (!user || !token || !needsReaccept) {
		if (justAccepted) {
			return (
				<div
					className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] bg-[#1A3C34] text-white px-4 py-2
							rounded-sm text-xs flex items-center gap-2 shadow-lg"
					data-testid="consent-banner-accepted"
				>
					<Check className="w-3.5 h-3.5" /> {t('consent.accepted_toast')}
				</div>
			);
		}
		return null;
	}

	return (
		<div
			className="fixed bottom-0 inset-x-0 z-[70] bg-[#1A3C34] text-white border-t-4 border-[#8B4513] shadow-2xl"
			data-testid="consent-banner"
			role="dialog"
			aria-labelledby="consent-banner-title"
		>
			<div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-col md:flex-row md:items-center gap-4">
				<div className="flex items-start gap-3 flex-1">
					<ScrollText className="w-6 h-6 text-[#D4C5A9] flex-shrink-0 mt-0.5" />
					<div>
						<h3
							id="consent-banner-title"
							className="text-sm font-semibold text-white font-['Playfair_Display'] mb-1"
						>
							{t('consent.title')}
						</h3>
						<p className="text-xs text-white/80 leading-relaxed">
							<Trans
								i18nKey="consent.body"
								components={{
									terms: (
										<Link
											to="/terms"
											target="_blank"
											rel="noopener noreferrer"
											className="text-[#D4C5A9] underline hover:text-white"
											data-testid="consent-link-terms"
										/>
									),
									privacy: (
										<Link
											to="/privacy"
											target="_blank"
											rel="noopener noreferrer"
											className="text-[#D4C5A9] underline hover:text-white"
											data-testid="consent-link-privacy"
										/>
									),
								}}
							/>
						</p>
					</div>
				</div>

				<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 sm:flex-shrink-0">
					<label className="flex items-center gap-2 cursor-pointer text-xs text-white/90 select-none">
						<Checkbox
							checked={agreed}
							onCheckedChange={(v) => setAgreed(!!v)}
							className="border-white/50 data-[state=checked]:bg-[#D4C5A9] data-[state=checked]:text-[#1A3C34]"
							data-testid="consent-banner-checkbox"
						/>
						{t('consent.checkbox_label')}
					</label>
					<Button
						size="sm"
						onClick={submit}
						disabled={!agreed || submitting}
						className="bg-[#D4C5A9] hover:bg-[#C5B492] text-[#1A3C34] font-semibold rounded-sm h-9 px-4 text-xs
								disabled:opacity-50 disabled:cursor-not-allowed"
						data-testid="consent-banner-accept-btn"
					>
						{submitting ? t('common.loading') : t('consent.accept_btn')}
					</Button>
				</div>
			</div>
		</div>
	);
}